/**
 * AuthProvider — JWT-bearer auth context (digital-sizer auth, replaces Stytch).
 *
 * On mount, reads the JWT from localStorage / impersonation sessionStorage and
 * resolves the current user via /auth/me. Exposes login (token persistence +
 * me-refresh), logout, and impersonation helpers. A 401 on any dsHttp call
 * (registered via setUnauthorizedHandler) clears state and bounces to /login.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { authApi } from "../api/authApi";
import {
  userActionClear,
  userActionSet,
} from "../redux/actions/userActions";
import {
  getAuthToken,
  isImpersonating as readIsImpersonating,
  setAuthToken,
  setImpersonationToken,
  setUnauthorizedHandler,
} from "../lib/digitalSizerHttp";
import { markSignedInBefore } from "../utils/storage/partnerStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(null);
      setImpersonating(false);
      dispatch(userActionClear());
      return;
    }
    try {
      const u = await authApi.me();
      setUser(u);
      setImpersonating(readIsImpersonating());
      // Mirror /auth/me into Redux so non-context consumers (e.g. the pipeline
      // "Show only my loans" filter) can read the signed-in user. /auth/me
      // returns `email`; expose it as `emailAddress` to match existing usage.
      dispatch(userActionSet({ ...u, emailAddress: u?.email }));
    } catch {
      setUser(null);
      setAuthToken(null);
      setImpersonationToken(null);
      setImpersonating(false);
      dispatch(userActionClear());
    }
  }, [dispatch]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthToken(null);
      setImpersonationToken(null);
      setUser(null);
      setImpersonating(false);
      dispatch(userActionClear());
      if (typeof window !== "undefined") {
        const current = window.location.pathname + window.location.search;
        if (window.location.pathname !== "/login") {
          navigate(`/login?redirect=${encodeURIComponent(current)}`, {
            replace: true,
          });
        }
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, dispatch]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(
    async (token, options) => {
      setAuthToken(token, options);
      // Remember that a real sign-in happened on this device (covers email-code,
      // password, and reset-link auto-login) so first-time prompts can hide for
      // returning users. Not stamped for impersonation.
      markSignedInBefore();
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(() => {
    try {
      authApi.logout().catch(() => undefined);
    } catch {
      // ignore
    }
    setAuthToken(null);
    setImpersonationToken(null);
    setUser(null);
    setImpersonating(false);
    dispatch(userActionClear());
  }, [dispatch]);

  const startImpersonation = useCallback(
    async (token) => {
      setImpersonationToken(token);
      await refresh();
    },
    [refresh],
  );

  const stopImpersonation = useCallback(async () => {
    setImpersonationToken(null);
    await refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isImpersonating: impersonating,
        login,
        logout,
        startImpersonation,
        stopImpersonation,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function useAuthOptional() {
  return useContext(AuthContext);
}

export default AuthProvider;
